import {
  isPostHogFeatureFlagKey,
  type PostHogFeatureFlagKey,
} from "@/lib/feature-flags";
import {
  isPostHogSurveyProgramKey,
  type PostHogSurveyProgramKey,
} from "@/lib/posthog/surveys";

export type PostHogRemoteConfigFlagKey =
  | "seoh-experience-registry"
  | "seoh-portal-novu-nudges"
  | "seoh-support-widget"
  | "seoh-survey-nps";

export type PostHogRemoteConfigSurface = "client_portal" | "pwa" | "web";

export type PostHogSupportWidgetPayload = {
  readonly kind: "support_widget";
  readonly launcher: "floating" | "hidden" | "inline";
  readonly maxOpenDelayMs: number;
  readonly placement: "bottom_left" | "bottom_right";
  readonly requireAuthenticatedPortal: boolean;
  readonly surfaces: readonly PostHogRemoteConfigSurface[];
};

export type PostHogSurveyTargetingPayload = {
  readonly kind: "survey_targeting";
  readonly cooldownDays: number;
  readonly locales: readonly string[];
  readonly minSessionPageDepth: number;
  readonly routePaths: readonly string[];
  readonly surfaces: readonly PostHogRemoteConfigSurface[];
  readonly surveyProgramKey: PostHogSurveyProgramKey;
};

export type PostHogPortalNudgePayload = {
  readonly kind: "portal_nudge";
  readonly channels: readonly ("internal" | "portal")[];
  readonly maxPerWeek: number;
  readonly minDaysSinceSignup: number;
  readonly topic: "billing" | "onboarding" | "retention" | "support";
  readonly variantKey: string;
};

export type PostHogExperienceRegistryPayload = {
  readonly kind: "experience_registry";
  readonly globalHoldoutPercent?: number;
  readonly holdoutVariantPrefix?: "holdout-";
  readonly liveTargetingLevel?: "member_segment";
  readonly maxLiveCells?: number;
  readonly mode: "observe" | "test";
  readonly registryVersion: string;
  readonly routePaths: readonly string[];
  readonly variants: readonly {
    readonly componentId: string;
    readonly variantKey: string;
    readonly weight: number;
  }[];
};

export type PostHogRemoteConfigPayload =
  | PostHogExperienceRegistryPayload
  | PostHogPortalNudgePayload
  | PostHogSupportWidgetPayload
  | PostHogSurveyTargetingPayload;

export const POSTHOG_REMOTE_CONFIG_FLAG_KEYS = [
  "seoh-experience-registry",
  "seoh-portal-novu-nudges",
  "seoh-support-widget",
  "seoh-survey-nps",
] as const satisfies readonly PostHogRemoteConfigFlagKey[];

const POSTHOG_REMOTE_CONFIG_FLAG_KEY_SET = new Set<string>(POSTHOG_REMOTE_CONFIG_FLAG_KEYS);
const SAFE_TOKEN_PATTERN = /^[a-z0-9][a-z0-9_.:/-]{0,119}$/i;
const SAFE_VERSION_PATTERN = /^[a-z0-9][a-z0-9_.-]{0,39}$/i;
const SAFE_LOCALE_PATTERN = /^[a-z]{2}(?:-[a-z0-9]{2,8})?$/;
const SENSITIVE_KEY_PATTERN =
  /email|phone|name|company|message|body|token|api[_-]?key|auth|password|secret|cookie|supabase|distinct|person|session|otp|magic/i;
const SENSITIVE_VALUE_PATTERN =
  /@|https?:\/\//i;
const PHONE_LIKE_PATTERN = /^\+?\d[\d\s().-]{7,}$/;
const SUPPORT_WIDGET_PAYLOAD_KEYS = new Set([
  "launcher",
  "maxOpenDelayMs",
  "placement",
  "requireAuthenticatedPortal",
  "surfaces",
]);
const SURVEY_TARGETING_PAYLOAD_KEYS = new Set([
  "cooldownDays",
  "locales",
  "minSessionPageDepth",
  "routePaths",
  "surfaces",
  "surveyProgramKey",
]);
const PORTAL_NUDGE_PAYLOAD_KEYS = new Set([
  "channels",
  "maxPerWeek",
  "minDaysSinceSignup",
  "topic",
  "variantKey",
]);
const EXPERIENCE_REGISTRY_PAYLOAD_KEYS = new Set([
  "globalHoldoutPercent",
  "holdoutVariantPrefix",
  "liveTargetingLevel",
  "maxLiveCells",
  "mode",
  "registryVersion",
  "routePaths",
  "variants",
]);

export function isPostHogRemoteConfigFlagKey(value: string): value is PostHogRemoteConfigFlagKey {
  return POSTHOG_REMOTE_CONFIG_FLAG_KEY_SET.has(value);
}

export function getPostHogRemoteConfigKindForFlag(
  key: PostHogFeatureFlagKey,
): PostHogRemoteConfigPayload["kind"] | undefined {
  if (key === "seoh-support-widget") return "support_widget";
  if (key === "seoh-survey-nps") return "survey_targeting";
  if (key === "seoh-portal-novu-nudges") return "portal_nudge";
  if (key === "seoh-experience-registry") return "experience_registry";
  return undefined;
}

export function parsePostHogFeatureFlagPayload(
  key: string,
  rawPayload: unknown,
): PostHogRemoteConfigPayload | undefined {
  if (!isPostHogFeatureFlagKey(key) || !isPostHogRemoteConfigFlagKey(key)) {
    return undefined;
  }

  const payload = normalizePayload(rawPayload);
  const allowedPayloadKeys = getAllowedPayloadKeysForFlag(key);
  if (!payload || !allowedPayloadKeys || containsSensitiveUnknownPayloadShape(payload, allowedPayloadKeys)) {
    return undefined;
  }

  if (key === "seoh-support-widget") return parseSupportWidgetPayload(payload);
  if (key === "seoh-survey-nps") return parseSurveyTargetingPayload(payload);
  if (key === "seoh-portal-novu-nudges") return parsePortalNudgePayload(payload);
  if (key === "seoh-experience-registry") return parseExperienceRegistryPayload(payload);
  return undefined;
}

function getAllowedPayloadKeysForFlag(key: PostHogRemoteConfigFlagKey): ReadonlySet<string> | undefined {
  if (key === "seoh-support-widget") return SUPPORT_WIDGET_PAYLOAD_KEYS;
  if (key === "seoh-survey-nps") return SURVEY_TARGETING_PAYLOAD_KEYS;
  if (key === "seoh-portal-novu-nudges") return PORTAL_NUDGE_PAYLOAD_KEYS;
  if (key === "seoh-experience-registry") return EXPERIENCE_REGISTRY_PAYLOAD_KEYS;
  return undefined;
}

function normalizePayload(rawPayload: unknown): Record<string, unknown> | undefined {
  if (typeof rawPayload === "string") {
    const trimmed = rawPayload.trim();
    if (!trimmed || trimmed.length > 6000) return undefined;
    try {
      return asPlainRecord(JSON.parse(trimmed));
    } catch {
      return undefined;
    }
  }

  return asPlainRecord(rawPayload);
}

function parseSupportWidgetPayload(payload: Record<string, unknown>): PostHogSupportWidgetPayload {
  return {
    kind: "support_widget",
    launcher: enumValue(payload.launcher, ["floating", "hidden", "inline"], "floating"),
    maxOpenDelayMs: integerValue(payload.maxOpenDelayMs, 0, 0, 30_000),
    placement: enumValue(payload.placement, ["bottom_left", "bottom_right"], "bottom_right"),
    requireAuthenticatedPortal: booleanValue(payload.requireAuthenticatedPortal, false),
    surfaces: surfaceList(payload.surfaces, ["web", "pwa", "client_portal"]),
  };
}

function parseSurveyTargetingPayload(payload: Record<string, unknown>): PostHogSurveyTargetingPayload | undefined {
  const surveyProgramKey = stringValue(payload.surveyProgramKey, "site_nps", 80);
  if (!isPostHogSurveyProgramKey(surveyProgramKey)) {
    return undefined;
  }

  return {
    kind: "survey_targeting",
    cooldownDays: integerValue(payload.cooldownDays, 30, 1, 365),
    locales: localeList(payload.locales),
    minSessionPageDepth: integerValue(payload.minSessionPageDepth, 2, 1, 20),
    routePaths: routePathList(payload.routePaths),
    surfaces: surfaceList(payload.surfaces, ["web"]),
    surveyProgramKey,
  };
}

function parsePortalNudgePayload(payload: Record<string, unknown>): PostHogPortalNudgePayload {
  return {
    kind: "portal_nudge",
    channels: enumList(payload.channels, ["internal", "portal"], ["portal"]),
    maxPerWeek: integerValue(payload.maxPerWeek, 1, 1, 7),
    minDaysSinceSignup: integerValue(payload.minDaysSinceSignup, 0, 0, 365),
    topic: enumValue(payload.topic, ["billing", "onboarding", "retention", "support"], "onboarding"),
    variantKey: stringValue(payload.variantKey, "default", 80),
  };
}

function parseExperienceRegistryPayload(payload: Record<string, unknown>): PostHogExperienceRegistryPayload | undefined {
  if (Array.isArray(payload.variants) && payload.variants.some(containsSensitiveShape)) {
    return undefined;
  }

  const variants = Array.isArray(payload.variants)
    ? payload.variants
        .map((variant) => asPlainRecord(variant))
        .filter((variant): variant is Record<string, unknown> => Boolean(variant))
        .slice(0, 25)
        .map((variant) => ({
          componentId: stringValue(variant.componentId, "", 120),
          variantKey: stringValue(variant.variantKey, "default", 80),
          weight: integerValue(variant.weight, 100, 0, 100),
        }))
        .filter((variant) => Boolean(variant.componentId))
    : [];

  const globalHoldoutPercent =
    payload.globalHoldoutPercent === undefined ? undefined : integerValue(payload.globalHoldoutPercent, 0, 0, 10);

  return {
    kind: "experience_registry",
    ...(globalHoldoutPercent
      ? { globalHoldoutPercent, holdoutVariantPrefix: enumValue(payload.holdoutVariantPrefix, ["holdout-"] as const, "holdout-") }
      : {}),
    liveTargetingLevel: enumValue(payload.liveTargetingLevel, ["member_segment"] as const, "member_segment"),
    maxLiveCells: integerValue(payload.maxLiveCells, 5, 1, 5),
    mode: enumValue(payload.mode, ["observe", "test"], "observe"),
    registryVersion: versionValue(payload.registryVersion, "repo-default"),
    routePaths: routePathList(payload.routePaths),
    variants,
  };
}

function containsSensitiveUnknownPayloadShape(value: Record<string, unknown>, allowedKeys: ReadonlySet<string>): boolean {
  return Object.entries(value).some(
    ([key, childValue]) => !allowedKeys.has(key) && (SENSITIVE_KEY_PATTERN.test(key) || containsSensitiveShape(childValue)),
  );
}

function asPlainRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  return value as Record<string, unknown>;
}

function containsSensitiveShape(value: unknown): boolean {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return SENSITIVE_VALUE_PATTERN.test(trimmed) || PHONE_LIKE_PATTERN.test(trimmed);
  }

  if (Array.isArray(value)) {
    return value.some(containsSensitiveShape);
  }

  if (value && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).some(
      ([key, childValue]) => SENSITIVE_KEY_PATTERN.test(key) || containsSensitiveShape(childValue),
    );
  }

  return false;
}

function stringValue(value: unknown, fallback: string, maxLength: number): string {
  if (typeof value !== "string") return fallback;
  const normalized = value.trim().replace(/[^\w .:/-]/g, "-").replace(/\s+/g, "-").slice(0, maxLength);
  if (!normalized || !SAFE_TOKEN_PATTERN.test(normalized) || containsSensitiveShape(normalized)) {
    return fallback;
  }
  return normalized;
}

function versionValue(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const normalized = value.trim().slice(0, 40);
  if (!normalized || !SAFE_VERSION_PATTERN.test(normalized) || containsSensitiveShape(normalized)) {
    return fallback;
  }
  return normalized;
}

function routePathList(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter(isSafeRoutePath))].slice(0, 30);
}

function localeList(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((item): item is string => typeof item === "string").map((item) => item.trim().toLowerCase()))]
    .filter((item) => SAFE_LOCALE_PATTERN.test(item))
    .slice(0, 20);
}

function surfaceList(value: unknown, fallback: readonly PostHogRemoteConfigSurface[]): readonly PostHogRemoteConfigSurface[] {
  const parsed = enumList(value, ["client_portal", "pwa", "web"], fallback);
  return parsed as readonly PostHogRemoteConfigSurface[];
}

function enumList<T extends string>(value: unknown, allowed: readonly T[], fallback: readonly T[]): readonly T[] {
  if (!Array.isArray(value)) return fallback;
  const allowedSet = new Set<string>(allowed);
  const parsed = [...new Set(value.filter((item): item is T => typeof item === "string" && allowedSet.has(item)))];
  return parsed.length ? parsed : fallback;
}

function enumValue<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === "string" && allowed.includes(value as T) ? (value as T) : fallback;
}

function integerValue(value: unknown, fallback: number, min: number, max: number): number {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < min || parsed > max) return fallback;
  return parsed;
}

function booleanValue(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function isSafeRoutePath(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return false;
  if (trimmed.includes("?") || trimmed.includes("#") || trimmed.length > 160) return false;
  return !containsSensitiveShape(trimmed);
}
